import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const OfficeDetails = ({ userData, setUserData }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [officeData, setOfficeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contactNumber: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    description: "",
  });


  useEffect(() => {
    const fetchOffice = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setError("Authentication required.");
        setLoading(false);
        return;
      }

      try {
        const response = await api.get("/offices", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success && response.data.data) {
          const office = response.data.data;
          setOfficeData(office);

          setFormData({
            name: office.name || "",
            email: userData.email || "",
            contactNumber: office.phone || "",
            address: office.address || "",
            city: office.city || "",
            state: office.state || "",
            zip: office.zip || "",
            description: office.description || "",
          });

        } else {
          setError("No office found.");
        }
      } catch (err) {
        const msg = err.response?.data?.message || "Failed to load office data.";
        setError(msg);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: msg,
          confirmButtonColor: "#1e40af",
        });
      } finally {
        setLoading(false);
      }
    };

    if (userData.isLawFirm) {
      fetchOffice();
    } else {
      setFormData({
        name: userData.name || "",
        email: userData.email || "",
        contactNumber: userData.phone || "",
        address: "",
        city: "",
        state: "",
        zip: "",
        description: "",
      });
      setLoading(false);
    }
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await Swal.fire({
      title: "Save Changes?",
      text: "Are you sure you want to update your office information?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1e40af",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setSaving(true);

    const token = localStorage.getItem("auth_token");
    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Authentication required.",
        confirmButtonColor: "#1e40af",
      });
      setSaving(false);
      return;
    }

    const payload = {
      name: formData.name,
      description: formData.description,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      phone: formData.contactNumber,
    };

    try {
      // PUT /offices بدون ID في الـ URL
      const response = await api.put("/offices", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        const updatedOffice = response.data.data;

        setOfficeData(updatedOffice);
        setUserData((prev) => ({
          ...prev,
          name: formData.name,
          email: formData.email,
          firmDetails: {
            contactNumber: formData.contactNumber,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
          },
        }));

        setIsEditing(false);

        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: "Office information updated successfully.",
          confirmButtonColor: "#1e40af",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update office.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#1e40af",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: officeData?.name || userData.name || "",
      email: userData.email || "",
      contactNumber: officeData?.phone || userData.phone || "",
      address: officeData?.address || "",
      city: officeData?.city || "",
      state: officeData?.state || "",
      zip: officeData?.zip || "",
      description: officeData?.description || "",
    });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <XCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
        <p className="text-red-700 font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
          <p className="text-gray-600 mt-1">Manage your account and office details</p>
        </div>
        {!isEditing && (
          <motion.button
            onClick={() => setIsEditing(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Edit Profile
          </motion.button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div className="w-full">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Office Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={!isEditing}
            className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
              ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              : "border-gray-200 bg-gray-50"
              }`}
            placeholder="Enter office name"
          />
        </div>

        {/* Email (Read-only) */}
        <div className="w-full">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            disabled
            className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-lg"
          />
        </div>

        {/* Office Details */}
        {userData.isLawFirm && (
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Office Details</h3>

            {/* Description */}
            <div className="w-full mb-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                disabled={!isEditing}
                rows={3}
                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                  ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  : "border-gray-200 bg-gray-50"
                  }`}
                placeholder="Brief description of your firm"
              />
            </div>

            {/* Contact Number */}
            <div className="w-full mb-6">
              <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Contact Number *
              </label>
              <input
                type="tel"
                id="contactNumber"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                  ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  : "border-gray-200 bg-gray-50"
                  }`}
                placeholder="Enter contact number"
              />
            </div>

            {/* Address */}
            <div className="w-full mb-6">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Address *
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={!isEditing}
                rows={2}
                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                  ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  : "border-gray-200 bg-gray-50"
                  }`}
                placeholder="Enter address"
              />
            </div>

            {/* City, State, ZIP */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    : "border-gray-200 bg-gray-50"
                    }`}
                  placeholder="City"
                />
              </div>
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    : "border-gray-200 bg-gray-50"
                    }`}
                  placeholder="State"
                />
              </div>
              <div>
                <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  id="zip"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    : "border-gray-200 bg-gray-50"
                    }`}
                  placeholder="ZIP"
                />
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {isEditing && (
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <motion.button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              disabled={saving}
              className={`px-6 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 transition-all duration-200 ${saving ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
                }`}
              whileHover={!saving ? { scale: 1.05 } : {}}
              whileTap={!saving ? { scale: 0.95 } : {}}
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </motion.button>
          </div>
        )}
      </form>

      {/* Account Information */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Account Type</h4>
            <p className="text-gray-600">{userData.isLawFirm ? "Law Firm" : "Individual"}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Member Since</h4>
            <p className="text-gray-600">
              {officeData?.created_at ? new Date(officeData.created_at).toLocaleDateString() : "—"}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Last Updated</h4>
            <p className="text-gray-600">
              {officeData?.updated_at ? new Date(officeData.updated_at).toLocaleDateString() : "—"}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Status</h4>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${officeData?.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}>
              {officeData?.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficeDetails;